import { Button } from '@insify/design-system/components';
import { IconicContract, IconicDocumentShield } from '@insify/design-system/iconic-icons';
import { Money } from '@insify/global-money';
import { useTranslation } from 'react-i18next';

import { useProfile } from '../../../api/queries/useProfile';
import { CoverageGrid } from '../../CoverageGrid';
import { InfoDisplay } from '../../InfoDisplay';
import { TypeformButton } from '../../TypeformButton/TypeformButton';
import { ProductDetailSection } from '../ProductDetailSection';
import styles from '../styles.module.css';

import type { CompanyBundle, PersonalBundle } from '../../../types/Policy';

interface CompanyProps {
  bundle: CompanyBundle;
}

interface PersonalProps {
  bundle: PersonalBundle;
}

const Company: React.FC<CompanyProps> = ({ bundle }) => {
  const { t } = useTranslation('policy');
  const { data: profile } = useProfile();
  const { email, phoneNumbers } = profile?.result?.personalInformation || {};

  const isCancelledPolicy = bundle.coverages && bundle.coverages.every((coverage) => coverage.status === 'cancelled');

  return (
    <div>
      <h2>{t('coverages')}</h2>
      <CoverageGrid coverages={bundle.coverages} policy={bundle} />
      {!isCancelledPolicy && (
        <div className={styles.buttonGroup}>
          <TypeformButton
            type='button'
            trackingId='cancelContract'
            formType='cancel_contract'
            policyType={bundle.type}
            kind='neutral'
            label={t('cancel_policy')}
            hiddenFields={{
              email: email,
              country: bundle.country,
              policy_number: bundle.coverages.at(0)?.policyNumber,
              phone: phoneNumbers?.at(0),
              name: bundle.companyName,
            }}
            leftIcon={<IconicContract />}
            className={styles.cancelButton}
            confirmation={t('cancel_policy_confirmation')}
          />
        </div>
      )}
    </div>
  );
};

const Personal: React.FC<PersonalProps> = ({ bundle }) => {
  const { t } = useTranslation('policy');
  const { data: profile } = useProfile();
  const { email, phoneNumbers, name } = profile?.result?.personalInformation || {};

  const isCancelledPolicy = bundle.coverages && bundle.coverages.every((coverage) => coverage.status === 'cancelled');

  const activeCoverage = bundle.coverages && bundle.coverages[0];
  if (!activeCoverage) {
    return <div>{t('no_active_coverage')}</div>;
  }

  const viewPolicy = () => {
    const { url = '' } = activeCoverage.documents?.length ? activeCoverage.documents[0] : {};
    window.open(url, '_blank');
  };

  return (
    <div>
      <ProductDetailSection data-hj-suppress title={t('policy_details.title')} subtitle={t('policy_details.subtitle')}>
        <InfoDisplay.Container>
          <InfoDisplay.Label>{t('policy_details.insured_amount')}</InfoDisplay.Label>
          <InfoDisplay.Text>
            {t('money_per_month', { amount: Money.fromField(activeCoverage.coverLimit).toFixed(2) })}
          </InfoDisplay.Text>
        </InfoDisplay.Container>

        <div className={styles.buttonGroup}>
          <Button
            type='button'
            kind='neutral'
            onClick={viewPolicy}
            trackingId='view-policy'
            className='insify-button'
            leftIcon={<IconicDocumentShield />}
            disabled={!activeCoverage.documents?.length}
          >
            {t('policy_details.view_policy')}
          </Button>
          {!isCancelledPolicy && (
            <TypeformButton
              type='button'
              trackingId='cancelContract'
              formType='cancel_contract'
              policyType={bundle.type}
              kind='neutral'
              label={t('cancel_policy')}
              hiddenFields={{
                email: email,
                country: bundle.country,
                policy_number: bundle.coverages.at(0)?.policyNumber,
                phone: phoneNumbers?.at(0),
                name: name,
              }}
              leftIcon={<IconicContract />}
              confirmation={t('cancel_policy_confirmation')}
            />
          )}
        </div>
        <div style={{ width: '100%', borderBottom: '1px solid #e0e0e0', margin: '24px 0' }} />
      </ProductDetailSection>
    </div>
  );
};

export const PolicyTab = {
  Personal,
  Company,
};
